import { round2 } from './round';

/**
 * Pure helpers for the bank-payment "split one transaction across several
 * invoices" dialog (`docs/PROMPT_agent-bank-payment-review-multi-invoice.md`).
 * No Angular, no I/O -- everything the dialog decides about amounts lives here
 * so it can be tested without a component.
 *
 * Every amount is rounded to cents before it is added up, so a split of
 * 100.10 + 200.20 never trips over float drift. Because sums are exact cents,
 * the total is compared with the bank amount STRICTLY: even one cent over is a
 * real over-allocation, not rounding noise. The 0.01 tolerance applies only to
 * an invoice's own debt figure (same as the review panel's `invoiceAlreadyPaid()`)
 * and to deciding when the greedy prefill has nothing left worth allocating.
 */
export const ALLOCATION_TOLERANCE = 0.01;

/** One candidate invoice as the greedy prefill sees it. */
export interface AllocationCandidate {
  /** Any stable identity (the invoice's DOCID). */
  key: number;
  /** Open debt on the invoice. */
  outstanding: number;
  /** True for the agent's own `matched_document_id` suggestion. */
  isSuggested: boolean;
  /** False for invoices that must not be pre-ticked. */
  selectable: boolean;
  /** Shipped but not yet paid (see {@link isShippedUnpaid}): ranked ahead of the rest. */
  shippedUnpaid?: boolean;
}

/** True when goods worth more than has been paid were already shipped against the invoice
 *  (`SHIPSUM - PAYDSUM`, both in the main currency): we delivered something the customer has not
 *  paid for yet. Unknown figures count as not shipped-unpaid. Whether the invoice is still "valid"
 *  (`DOCDATE < DOCVALDATE`) deliberately plays no part -- shipped ranks first either way. */
export function isShippedUnpaid(shippedSum: number | null | undefined, paidSum: number | null | undefined): boolean {
  if (shippedSum == null || isNaN(shippedSum)) {
    return false;
  }
  return round2(shippedSum - (paidSum || 0)) > ALLOCATION_TOLERANCE;
}

/**
 * The order invoices are offered in, and pre-ticked in: the agent's own suggestion first (the
 * customer named it in the remittance), then shipped-but-unpaid invoices, then the rest. Each
 * group keeps the caller's order (the API returns oldest first). Stable, does not mutate.
 */
export function rankCandidates<T extends { isSuggested: boolean; shippedUnpaid?: boolean }>(items: T[]): T[] {
  const tier = (c: T) => c.isSuggested ? 0 : (c.shippedUnpaid ? 1 : 2);
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (tier(a.item) - tier(b.item)) || (a.index - b.index))
    .map(x => x.item);
}

/** Share of the whole invoice sum that `amount` covers, in percent rounded to one decimal
 *  (78.4, 78, 100). `null` when there is no payment amount or no usable invoice sum. */
export function paymentPercent(amount: number | null | undefined, documentSum: number | null | undefined): number | null {
  if (amount == null || isNaN(amount) || !(amount > 0) || documentSum == null || !(documentSum > 0)) {
    return null;
  }
  return Math.round(amount / documentSum * 1000) / 10;
}

/** What the dialog pre-ticks: which invoices, and how much for each. */
export function greedyAllocate(candidates: AllocationCandidate[], bankAmount: number): Map<number, number> {
  const amounts = new Map<number, number>();
  let remaining = round2(bankAmount);

  // Suggestion first, then shipped-but-unpaid, then the rest, each in the caller's order.
  const ordered = rankCandidates(candidates);

  for (const c of ordered) {
    if (remaining <= ALLOCATION_TOLERANCE) {
      break;
    }
    if (!c.selectable || !(c.outstanding > ALLOCATION_TOLERANCE)) {
      continue;
    }
    const take = round2(Math.min(c.outstanding, remaining));
    amounts.set(c.key, take);
    remaining = round2(remaining - take);
  }
  return amounts;
}

/** The amount a freshly ticked invoice starts with: as much of its debt as the
 *  unallocated remainder can cover, `0` when nothing is left. */
export function defaultAmount(outstanding: number, remaining: number): number {
  if (remaining <= ALLOCATION_TOLERANCE) {
    return 0;
  }
  return round2(Math.min(outstanding, remaining));
}

/** One row of the dialog as the validator sees it. */
export interface AllocationLine {
  checked: boolean;
  amount: number | null;
  outstanding: number;
}

export interface AllocationValidation {
  /** True when the split can be run. */
  ok: boolean;
  /** One entry per input line: the reason it is invalid, or `null`. Unticked lines are always `null`. */
  lineErrors: (string | null)[];
  /** A problem with the split as a whole, or `null`. */
  totalError: string | null;
  /** Sum of the ticked lines' amounts. */
  allocated: number;
  /** Bank amount not (yet) allocated; negative when over-allocated. Shown as plain information, never tracked. */
  remaining: number;
}

/**
 * Rules: at least one ticked invoice; every ticked amount above 0 and no more
 * than that invoice's debt (+ tolerance); the total no more than the bank
 * amount, with no tolerance (over-allocation blocks, even by one cent).
 * Under-allocation is allowed -- the remainder is only shown.
 */
export function validateAllocation(lines: AllocationLine[], bankAmount: number): AllocationValidation {
  let allocated = 0;
  let checkedCount = 0;

  const lineErrors = lines.map(line => {
    if (!line.checked) {
      return null;
    }
    checkedCount++;
    const amount = line.amount;
    if (amount == null || isNaN(amount) || !(amount > 0)) {
      return 'Вкажіть суму більше 0';
    }
    allocated = round2(allocated + amount);
    if (amount > line.outstanding + ALLOCATION_TOLERANCE) {
      return 'Більше за залишок рахунку';
    }
    return null;
  });

  const remaining = round2(bankAmount - allocated);
  let totalError: string | null = null;
  if (checkedCount === 0) {
    totalError = 'Оберіть хоча б один рахунок';
  } else if (remaining < 0) {
    totalError = 'Розподілено більше, ніж сума транзакції';
  }

  return {
    ok: totalError === null && lineErrors.every(e => e === null),
    lineErrors,
    totalError,
    allocated,
    remaining,
  };
}

/** Execution order: the agent's suggested invoice first (so an ordinary split
 *  records no spurious "manager overrode the suggestion" on the bank row), the
 *  rest in their given order. Stable. */
export function orderForExecution<T extends { isSuggested: boolean }>(lines: T[]): T[] {
  return [...lines.filter(l => l.isSuggested), ...lines.filter(l => !l.isSuggested)];
}
